import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { ArrowBack, CheckCircle, Cancel, ExpandMore, People, QueryStats } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import principalAPI from '../../services/principalAPI';
import { useAuth } from '../../context/AuthContext';
import MobileFilterSheet from '@components/mobile/MobileFilterSheet';

const Attendance = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedFilter, setSelectedFilter] = useState('all'); // 'all', 'present', 'absent'
  const [selectedClass, setSelectedClass] = useState('all'); // 'all' or class id
  const [attendanceData, setAttendanceData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAttendance = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await principalAPI.getStudentAttendance(selectedDate);
      setAttendanceData(response.data.classes || []);
    } catch (error) {
      console.error('Error fetching attendance:', error);
      setError('Failed to load attendance data. Please try again.');
      setAttendanceData([]);
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => {
    fetchAttendance();
  }, [fetchAttendance]);

  // Calculate statistics
  const calculateStats = () => {
    let total = 0;
    let present = 0;
    let absent = 0;

    attendanceData.forEach(cls => {
      cls.students.forEach(student => {
        total++;
        if (student.status === 'present') present++;
        if (student.status === 'absent') absent++;
      });
    });

    return {
      total,
      present,
      absent,
      percentage: total > 0 ? Math.round((present / total) * 100) : 0
    };
  };

  const stats = calculateStats();

  // Filter students based on selected filter and class
  const getFilteredData = () => {
    let data = attendanceData;

    // Filter by class first
    if (selectedClass !== 'all') {
      data = data.filter(cls => cls.class?.id === selectedClass);
    }

    // Then filter by status
    if (selectedFilter === 'all') {
      return data;
    }

    return data.map(cls => ({
      ...cls,
      students: cls.students.filter(student => student.status === selectedFilter)
    })).filter(cls => cls.students.length > 0);
  };

  const filteredData = getFilteredData();

  // Get unique classes for the dropdown
  const availableClasses = attendanceData.filter(cls => cls.class);

  const handleCardClick = (filter) => {
    setSelectedFilter(filter);
  };

  const getCardStyle = (filter) => ({
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    transform: selectedFilter === filter ? 'scale(1.05)' : 'scale(1)',
    boxShadow: selectedFilter === filter ? 4 : 2,
    border: selectedFilter === filter ? '2px solid' : 'none',
    '&:hover': {
      transform: 'scale(1.05)',
      boxShadow: 4
    }
  });

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 2, md: 4 }, mb: 4, px: { xs: 1.5, sm: 3 } }}>
      <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} gap={2} mb={3}>
        <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
          View Attendance
        </Typography>
        <Box display="flex" gap={1} flexWrap="wrap">
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={() => navigate('/principal/dashboard')}
          >
            Back to Dashboard
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: 3, mb: 3 }}>
        <MobileFilterSheet label="Date & Class" activeCount={selectedClass !== 'all' ? 1 : 0}>
          <Box display="flex" gap={2} alignItems="center" mb={3} flexWrap="wrap">
            <TextField
              type="date"
              label="Select Date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 200, width: { xs: '100%', sm: 'auto' } }}
            />
            <FormControl sx={{ minWidth: 200, width: { xs: '100%', sm: 'auto' } }}>
              <InputLabel>Filter by Class</InputLabel>
              <Select
                value={selectedClass}
                label="Filter by Class"
                onChange={(e) => setSelectedClass(e.target.value)}
              >
                <MenuItem value="all">All Classes</MenuItem>
                {availableClasses.map((classData) => {
                  const className = classData.class?.name ||
                    (classData.class?.grade && classData.class?.section
                      ? `${classData.class.grade} - ${classData.class.section}`
                      : classData.class?.grade || classData.class?.section || 'Unknown');
                  return (
                    <MenuItem key={classData.class.id} value={classData.class.id}>
                      {className}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              onClick={fetchAttendance}
              disabled={loading}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              {loading ? <CircularProgress size={20} /> : 'Refresh'}
            </Button>
          </Box>
        </MobileFilterSheet>

        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Click on a card to filter students by status
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>Loading attendance data...</Typography>
          </Box>
        ) : (
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <Card 
                elevation={selectedFilter === 'all' ? 8 : 2}
                sx={getCardStyle('all')}
                onClick={() => handleCardClick('all')}
              >
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="text.secondary" gutterBottom>
                      Total Students
                    </Typography>
                    <Typography variant="h3" fontWeight="bold">
                      {stats.total}
                    </Typography>
                  </Box>
                  <People sx={{ fontSize: 50, color: 'primary.main', opacity: 0.7 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card 
              elevation={selectedFilter === 'present' ? 8 : 2}
              sx={getCardStyle('present')}
              onClick={() => handleCardClick('present')}
            >
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="text.secondary" gutterBottom>
                      Present
                    </Typography>
                    <Typography variant="h3" fontWeight="bold" color="success.main">
                      {stats.present}
                    </Typography>
                  </Box>
                  <CheckCircle sx={{ fontSize: 50, color: 'success.main', opacity: 0.7 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card 
              elevation={selectedFilter === 'absent' ? 8 : 2}
              sx={getCardStyle('absent')}
              onClick={() => handleCardClick('absent')}
            >
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="text.secondary" gutterBottom>
                      Absent
                    </Typography>
                    <Typography variant="h3" fontWeight="bold" color="error.main">
                      {stats.absent}
                    </Typography>
                  </Box>
                  <Cancel sx={{ fontSize: 50, color: 'error.main', opacity: 0.7 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={2}>
              <CardContent>
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Attendance %
                  </Typography>
                  <Typography variant="h3" fontWeight="bold" color="info.main">
                    {stats.percentage}%
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        )}

      </Paper>

      <Paper sx={{ p: 3, mt: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" gutterBottom fontWeight="bold">
            {selectedFilter === 'all' && 'All Students'}
            {selectedFilter === 'present' && 'Present Students'}
            {selectedFilter === 'absent' && 'Absent Students'}
            {' '}by Class
          </Typography>
          {selectedFilter !== 'all' && (
            <Chip 
              label={`Showing ${selectedFilter} only`}
              color={selectedFilter === 'present' ? 'success' : 'error'}
              onDelete={() => setSelectedFilter('all')}
            />
          )}
        </Box>

        {filteredData.length === 0 ? (
          <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
            No {selectedFilter === 'all' ? 'students' : selectedFilter + ' students'} found
          </Typography>
        ) : (
          filteredData.map((classData, index) => (
            <Accordion key={index} defaultExpanded={index === 0}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                  <Typography variant="h6" sx={{ flexGrow: 1 }}>
                    {classData.class?.name || 
                      (classData.class?.grade && classData.class?.section 
                        ? `${classData.class.grade} - ${classData.class.section}`
                        : classData.class?.grade || classData.class?.section || 'Unknown Class')}
                  </Typography>
                  <Chip 
                    label={`${classData.students.length} student${classData.students.length !== 1 ? 's' : ''}`}
                    size="small"
                    color="primary"
                  />
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Student Name</strong></TableCell>
                        <TableCell align="center"><strong>Status</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {classData.students.map((student) => (
                        <TableRow key={student.id}>
                          <TableCell>{student.name}</TableCell>
                          <TableCell align="center">
                            <Chip
                              label={student.status.charAt(0).toUpperCase() + student.status.slice(1)}
                              color={student.status === 'present' ? 'success' : 'error'}
                              size="small"
                              icon={student.status === 'present' ? <CheckCircle /> : <Cancel />}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          ))
        )}
      </Paper>
    </Container>
  );
};

export default Attendance;
